import React, { useState, useEffect, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Card,
  CardContent,
  Grid,
  Box,
  Button,
  Paper,
  Chip,
  Avatar,
  MenuItem,
  CircularProgress,
  Alert,
  Fade,
  Tabs,
  Tab,
  Fab,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Thermostat,
  WaterDrop,
  Air,
  Speed,
  Compress,
  WbSunny,
  LocationOn,
  BugReport,
  Settings,
  Dashboard as DashboardIcon,
  Map as MapIcon,
  Add,
  TableChart,
  Search,
  FilterList
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { stationService, dashboardService, stationTimeseriesService } from '../services/api';
import { convertToCSV, downloadCSV, generateCSVFilename } from '../utils/csvExport';
import MapView from './MapView';
import StationDetails from './StationDetails';
import AddStationDialog from './AddStationDialog';
import StationConfigDialog from './StationConfigDialog';
import { SUPPORTED_METRICS } from '../config/sensorMetrics';

interface Location {
  latitude: number;
  longitude: number;
}

interface Station {
  uuid: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at?: string;
  location: Location;
  elevation?: number;
  notes?: string;
  owner?: string;
  public: boolean;
  source?: string;
  timezone?: string;
  status: string;
  debug: boolean;
  latest_readings?: Record<string, any>;
  user_email?: string;
  tags?: string[];
}

interface DashboardStats {
  total_stations: number;
  active_stations: number;
  disabled_stations: number;
  debug_stations: number;
  data_points_per_second: number;
  active_alerts: number;
  total_data_points_today: number;
  last_updated: string;
  system_status: string;
}

// Metric display configuration
// Use centralized metric configuration
const metricConfig: Record<string, { name: string; color: string; unit: string }> = (() => {
  const config: Record<string, { name: string; color: string; unit: string }> = {};
  SUPPORTED_METRICS.forEach(metric => {
    config[metric.internal] = {
      name: metric.userFriendly.charAt(0).toUpperCase() + metric.userFriendly.slice(1).replace(/_/g, ' '),
      color: metric.color,
      unit: metric.unit
    };
  });
  return config;
})();

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { keycloak, initialized } = useKeycloak();
  const [stations, setStations] = useState<Station[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [tabValue, setTabValue] = useState(0);
  const [selectedStationUuid, setSelectedStationUuid] = useState<string>('');
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [initialDialogTab, setInitialDialogTab] = useState(0);
  const [addStationDialogOpen, setAddStationDialogOpen] = useState(false);
  const [addStationLocation, setAddStationLocation] = useState<{ lat: number; lng: number } | undefined>(undefined);
  const [selectedMapStationUuid, setSelectedMapStationUuid] = useState<string>('');
  const [selectedMapStation, setSelectedMapStation] = useState<Station | null>(null);
  const [mapTimeSeriesData, setMapTimeSeriesData] = useState<any[]>([]);
  const [mapTimeRange, setMapTimeRange] = useState<number>(24);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selectedConfigStation, setSelectedConfigStation] = useState<Station | undefined>(undefined);
  const [warningSnackbarOpen, setWarningSnackbarOpen] = useState(false);
  const dataFetched = useRef(false);

  // Table View state
  const [tableData, setTableData] = useState<any[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterStation, setFilterStation] = useState<string>('all');
  const [filterMetric, setFilterMetric] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [tableTimeRange, setTableTimeRange] = useState<number>(24);
  const [filterTag, setFilterTag] = useState<string>('all');

  // Get user info from Keycloak token
  const userEmail = keycloak.tokenParsed?.email || keycloak.tokenParsed?.preferred_username || 'User';

  const handleMapTimeRangeChange = (event: any) => {
    setMapTimeRange(event.target.value);
  };

  const getMapTimeRangeLabel = () => {
    if (mapTimeRange === 24) return '24-Hour';
    if (mapTimeRange === 48) return '48-Hour';
    if (mapTimeRange === 120) return '5-Day';
    if (mapTimeRange === 720) return 'Monthly';
    return `${mapTimeRange}-Hour`;
  };

  useEffect(() => {
    // Wait for Keycloak to initialize
    if (!initialized) {
      return;
    }

    // Check if user is authenticated via Keycloak
    if (!keycloak.authenticated) {
      navigate('/login');
      return;
    }

    // Prevent duplicate calls by checking if data has already been fetched
    if (!dataFetched.current) {
      fetchData();
    }
  }, [initialized, keycloak.authenticated]); // Depend on Keycloak state

  const fetchData = async () => {
    try {
      const [stationsData, statsData] = await Promise.all([
        // Only fetch user's own stations (include_public: false for security)
        stationService.getAll({ include_public: false }),
        dashboardService.getStats()
      ]);

      setStations(stationsData);
      setStats(statsData);
      dataFetched.current = true; // Mark data as fetched
    } catch (err) {
      setError('Network error while fetching data');
    } finally {
      setLoading(false);
    }
  };

  // Refetch map time series data when time range changes
  useEffect(() => {
    if (selectedMapStationUuid && tabValue === 1) {
      fetchMapTimeSeriesData(selectedMapStationUuid);
    }
  }, [mapTimeRange]);

  const handleLogout = () => {
    // Clear localStorage (legacy)
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_email');

    // Logout from Keycloak
    keycloak.logout({
      redirectUri: window.location.origin
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleStationSelect = (stationUuid: string, tab: number = 0) => {
    setSelectedStationUuid(stationUuid);
    setInitialDialogTab(tab);
    setDetailsDialogOpen(true);
  };

  const handleMapStationSelect = (stationUuid: string) => {
    setSelectedMapStationUuid(stationUuid);
    fetchMapStationDetails(stationUuid);
    fetchMapTimeSeriesData(stationUuid);
  };

  const fetchMapStationDetails = async (stationUuid: string) => {
    try {
      const data = await stationService.getById(stationUuid);
      setSelectedMapStation(data);
    } catch (error) {
      console.error('Error fetching map station details:', error);
    }
  };

  const fetchMapTimeSeriesData = async (stationUuid: string) => {
    try {
      const data = await stationTimeseriesService.getTimeseries(stationUuid, mapTimeRange);
      const formattedData = data.timeseries.map((point: any) => ({
          ...point,
          time: new Date(point.timestamp).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
          })
        }));
        setMapTimeSeriesData(formattedData);
    } catch (error) {
      console.error('Error fetching map time series data:', error);
    }
  };

  const handleAddStation = (location: { lat: number; lng: number }) => {
    setAddStationLocation(location);
    setAddStationDialogOpen(true);
  };

  const handleCreateStation = async (stationData: any): Promise<boolean> => {
    try {
      await stationService.create(stationData);
      fetchData(); // Refresh the data
      return true;
    } catch (error: any) {
      console.error('Error creating station:', error);
      throw error;
    }
  };

  const handleEditStation = async (stationUuid: string, stationData: Partial<Station>) => {
    try {
      // Call the API to update the station
      await stationService.update(stationUuid, stationData);
      console.log('Station updated successfully');
      
      // Refresh data after edit
      await fetchData();
      setConfigDialogOpen(false);
    } catch (error) {
      console.error('Error editing station:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleDeleteStation = async (stationUuid: string, withExport: boolean) => {
    try {
      if (withExport) {
        // Export to CSV first before deletion
        await handleExportStation(stationUuid);
        // Give a small delay to ensure export completes
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      // Call the API to delete the station
      await stationService.delete(stationUuid);
      console.log('Station deleted successfully:', stationUuid);
      
      // Refresh data after deletion
      await fetchData();
      setConfigDialogOpen(false);
    } catch (error) {
      console.error('Error deleting station:', error);
    }
  };

  const handleExportStation = async (stationUuid: string) => {
    try {
      const station = stations.find(s => s.uuid === stationUuid);
      if (!station) {
        console.error('Station not found');
        return;
      }

      // Get timeseries data for the last 30 days
      const timeseriesData = await stationTimeseriesService.getTimeseries(station.uuid, 24 * 30);
      
      // Convert to CSV format
      const csvData = convertToCSV({
        id: station.uuid,
        name: station.name,
        description: station.description,
        timeseries: timeseriesData.timeseries,
        period_hours: timeseriesData.period_hours,
        data_points: timeseriesData.data_points
      });
      
      // Download CSV file
      const filename = generateCSVFilename(station.name, station.uuid);
      downloadCSV(csvData, filename);
      
      console.log('Station data exported to CSV:', filename);
    } catch (error) {
      console.error('Error exporting station data:', error);
    }
    setConfigDialogOpen(false);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return 'success';
      case 'debug': return 'warning';
      case 'disabled': return 'error';
      default: return 'default';
    }
  };

  const getSensorIcon = (sensorType: string) => {
    switch (sensorType.toLowerCase()) {
      case 'temperature': return <Thermostat />;
      case 'humidity': 
      case 'rain': 
      case 'reference_et': return <WaterDrop />;
      case 'wind_speed': return <Air />;
      case 'atmospheric_pressure': return <Compress />;
      case 'solar_radiation': return <WbSunny />;
      default: return <Speed />;
    }
  };

  const formatReading = (key: string, reading: any) => {
    const sensorNames: Record<string, string> = {
      temperature: 'Temperature',
      humidity: 'Humidity',
      rain: 'Rainfall',
      wind_speed: 'Wind Speed',
      wind_direction: 'Wind Direction',
      atmospheric_pressure: 'Pressure',
      reference_et: 'Reference ET',
      solar_radiation: 'Solar Radiation'
    };
    return sensorNames[key] || key.charAt(0).toUpperCase() + key.slice(1);
  };

  // Table View handlers
  const fetchTableData = async () => {
    setTableLoading(true);
    try {
      // Fetch time series data for all stations
      const allData: any[] = [];

      for (const station of stations) {
        try {
          const data = await stationTimeseriesService.getTimeseries(station.uuid, tableTimeRange);
          data.timeseries.forEach((point: any) => {
            // Convert each measurement to a row
            Object.keys(point).forEach(key => {
              if (key !== 'timestamp' && point[key] !== null && point[key] !== undefined) {
                const config = metricConfig[key];
                if (config) {
                  allData.push({
                    stationUuid: station.uuid,
                    stationName: station.name,
                    timestamp: point.timestamp,
                    metric: key,
                    metricName: config.name,
                    value: point[key],
                    unit: config.unit,
                  });
                }
              }
            });
          });
        } catch (error) {
          console.error(`Error fetching data for station ${station.name}:`, error);
        }
      }

      // Sort by timestamp descending (newest first)
      allData.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      setTableData(allData);
    } catch (error) {
      console.error('Error fetching table data:', error);
    } finally {
      setTableLoading(false);
    }
  };

  // Fetch table data when switching to table view or time range changes
  useEffect(() => {
    if (tabValue === 2 && stations.length > 0) {
      fetchTableData();
    }
  }, [tabValue, tableTimeRange, stations]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterStationChange = (event: any) => {
    setFilterStation(event.target.value);
    setPage(0);
  };

  const handleFilterMetricChange = (event: any) => {
    setFilterMetric(event.target.value);
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleTableTimeRangeChange = (event: any) => {
    setTableTimeRange(event.target.value);
    setPage(0);
  };

  // Filter table data based on filters and search
  const getFilteredTableData = () => {
    return tableData.filter(row => {
      // Filter by station
      if (filterStation !== 'all' && row.stationUuid !== filterStation) {
        return false;
      }

      // Filter by metric
      if (filterMetric !== 'all' && row.metric !== filterMetric) {
        return false;
      }

      // Filter by search query
      if (searchQuery && !row.stationName.toLowerCase().includes(searchQuery.toLowerCase()) &&
          !row.metricName.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }

      return true;
    });
  };

  // Get unique metrics from table data
  const getUniqueMetrics = () => {
    const metrics = new Set(tableData.map(row => row.metric));
    return Array.from(metrics);
  };

  // Get all unique tags from all stations
  const getAllTags = () => {
    const allTags = new Set<string>();
    stations.forEach(station => {
      if (station.tags && Array.isArray(station.tags)) {
        station.tags.forEach(tag => allTags.add(tag));
      }
    });
    return Array.from(allTags).sort();
  };

  // Filter stations by selected tag
  const getFilteredStations = () => {
    if (filterTag === 'all') {
      return stations;
    }
    return stations.filter(station =>
      station.tags && Array.isArray(station.tags) && station.tags.includes(filterTag)
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading SenCore Dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Modern Navigation Header */}
      <AppBar 
        position="static" 
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.95) 0%, rgba(22, 33, 62, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0, 212, 255, 0.1)',
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, justifyContent: 'center' }}>
            <Typography variant="h6" component="div" sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #00d4ff 0%, #ff6b6b 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
            }}>
              SenCore Platform Environmental Monitoring System
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {/* Modern Navigation Tabs */}
        <Box sx={{ 
          mb: 4,
          background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.8) 0%, rgba(22, 33, 62, 0.8) 100%)',
          borderRadius: 3,
          border: '1px solid rgba(255, 255, 255, 0.05)',
          p: 1
        }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: '#00d4ff',
                height: 4,
                borderRadius: 2,
                bottom: 8,
              },
              '& .MuiTab-root': {
                borderRadius: 2,
                margin: 0.5,
                minHeight: 56,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(0, 212, 255, 0.1)',
                  color: '#00d4ff',
                },
              },
            }}
          >
            <Tab
              icon={<DashboardIcon />}
              label="Dashboard"
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab
              icon={<MapIcon />}
              label="Map View"
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab
              icon={<TableChart />}
              label="Table View"
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
          </Tabs>
        </Box>

        {/* Dashboard Content */}
        {tabValue === 0 && (
          <>
            {/* Modern Dashboard Stats */}
            {stats && (
              <Fade in timeout={1000}>
                <Grid container spacing={3} sx={{ mb: 5 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ 
                      background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 153, 204, 0.05) 100%)',
                      border: '1px solid rgba(0, 212, 255, 0.2)',
                      position: 'relative',
                      overflow: 'visible',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: 'linear-gradient(90deg, #00d4ff, #33dcff)',
                        borderRadius: '16px 16px 0 0',
                      }
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ 
                            p: 1.5, 
                            borderRadius: 2, 
                            background: 'linear-gradient(135deg, #00d4ff, #33dcff)',
                            mr: 2 
                          }}>
                            <DashboardIcon sx={{ color: '#000', fontSize: 20 }} />
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Total Stations
                          </Typography>
                        </Box>
                        <Typography variant="h3" sx={{
                          fontWeight: 700,
                          background: 'linear-gradient(135deg, #00d4ff, #33dcff)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          color: 'transparent',
                          mb: 1
                        }}>
                          {stats.total_stations || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Active monitoring points
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ 
                      background: 'linear-gradient(135deg, rgba(0, 245, 160, 0.1) 0%, rgba(0, 200, 118, 0.05) 100%)',
                      border: '1px solid rgba(0, 245, 160, 0.2)',
                      position: 'relative',
                      overflow: 'visible',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: 'linear-gradient(90deg, #00f5a0, #4fffb4)',
                        borderRadius: '16px 16px 0 0',
                      }
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ 
                            p: 1.5, 
                            borderRadius: 2, 
                            background: 'linear-gradient(135deg, #00f5a0, #4fffb4)',
                            mr: 2 
                          }}>
                            <WbSunny sx={{ color: '#000', fontSize: 20 }} />
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Online Stations
                          </Typography>
                        </Box>
                        <Typography variant="h3" sx={{
                          fontWeight: 700,
                          background: 'linear-gradient(135deg, #00f5a0, #4fffb4)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          color: 'transparent',
                          mb: 1
                        }}>
                          {stats.active_stations || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Operational systems
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ 
                      background: 'linear-gradient(135deg, rgba(55, 66, 250, 0.1) 0%, rgba(83, 82, 237, 0.05) 100%)',
                      border: '1px solid rgba(55, 66, 250, 0.2)',
                      position: 'relative',
                      overflow: 'visible',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: 'linear-gradient(90deg, #3742fa, #5352ed)',
                        borderRadius: '16px 16px 0 0',
                      }
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ 
                            p: 1.5, 
                            borderRadius: 2, 
                            background: 'linear-gradient(135deg, #3742fa, #5352ed)',
                            mr: 2 
                          }}>
                            <Speed sx={{ color: '#fff', fontSize: 20 }} />
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Data Points/sec
                          </Typography>
                        </Box>
                        <Typography variant="h3" sx={{
                          fontWeight: 700,
                          background: 'linear-gradient(135deg, #3742fa, #5352ed)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          color: 'transparent',
                          mb: 1
                        }}>
                          {(stats.data_points_per_second || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Real-time throughput
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ 
                      background: 'linear-gradient(135deg, rgba(255, 184, 0, 0.1) 0%, rgba(204, 147, 0, 0.05) 100%)',
                      border: '1px solid rgba(255, 184, 0, 0.2)',
                      position: 'relative',
                      overflow: 'visible',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: 'linear-gradient(90deg, #ffb800, #ffc947)',
                        borderRadius: '16px 16px 0 0',
                      }
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ 
                            p: 1.5, 
                            borderRadius: 2, 
                            background: 'linear-gradient(135deg, #ffb800, #ffc947)',
                            mr: 2 
                          }}>
                            <BugReport sx={{ color: '#000', fontSize: 20 }} />
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Active Alerts
                          </Typography>
                        </Box>
                        <Typography variant="h3" sx={{
                          fontWeight: 700,
                          background: 'linear-gradient(135deg, #ffb800, #ffc947)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          color: 'transparent',
                          mb: 1
                        }}>
                          {stats.active_alerts || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Require attention
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Fade>
            )}

            {/* Modern Section Header */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-start', 
              mb: 4,
              p: 4,
              background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.02) 0%, rgba(255, 107, 107, 0.02) 100%)',
              borderRadius: 3,
              border: '1px solid rgba(255, 255, 255, 0.05)',
            }}>
              <Box>
                <Typography variant="h3" sx={{ 
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #00d4ff 0%, #ff6b6b 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  mb: 1
                }}>
                  Environmental Stations
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  Real-time monitoring network with UUID-based data ingestion
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    label={`${stations.length} Active`} 
                    size="small" 
                    sx={{ 
                      backgroundColor: 'rgba(0, 245, 160, 0.1)',
                      color: '#00f5a0',
                      border: '1px solid rgba(0, 245, 160, 0.3)',
                      fontWeight: 500
                    }}
                  />
                  <Chip 
                    label="Real-time" 
                    size="small" 
                    sx={{ 
                      backgroundColor: 'rgba(0, 212, 255, 0.1)',
                      color: '#00d4ff',
                      border: '1px solid rgba(0, 212, 255, 0.3)',
                      fontWeight: 500
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<Settings />}
                  onClick={() => {
                    if (!selectedStationUuid) {
                      setWarningSnackbarOpen(true);
                      return;
                    }
                    setSelectedConfigStation(stations.find(s => s.uuid === selectedStationUuid));
                    setConfigDialogOpen(true);
                  }}
                  sx={{ 
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'text.secondary',
                    '&:hover': {
                      borderColor: 'rgba(0, 212, 255, 0.5)',
                      backgroundColor: 'rgba(0, 212, 255, 0.04)',
                    }
                  }}
                >
                  Configure
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setAddStationDialogOpen(true)}
                  sx={{ 
                    background: 'linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)',
                    boxShadow: '0 8px 25px -8px rgba(0, 212, 255, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #33dcff 0%, #00b8e6 100%)',
                      boxShadow: '0 12px 35px -8px rgba(0, 212, 255, 0.4)',
                    }
                  }}
                >
                  Add Station
                </Button>
              </Box>
            </Box>

            {/* Tag Filter */}
            <Box sx={{ mb: 3 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="filter-tag-label">Filter by Tag</InputLabel>
                <Select
                  labelId="filter-tag-label"
                  id="filter-tag-select"
                  value={filterTag}
                  onChange={(e) => setFilterTag(e.target.value)}
                  label="Filter by Tag"
                >
                  <MenuItem value="all">All Tags</MenuItem>
                  {getAllTags().map(tag => (
                    <MenuItem key={tag} value={tag}>
                      {tag}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

        <Grid container spacing={3}>
          {getFilteredStations().map((station, index) => (
            <Grid item xs={12} md={6} lg={4} key={station.uuid}>
              <Fade in timeout={1000 + index * 200}>
                <Paper 
                  elevation={2} 
                  onClick={() => setSelectedStationUuid(station.uuid)}
                  sx={{ 
                    p: 3, 
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: selectedStationUuid === station.uuid ? '2px solid #00d4ff' : '2px solid transparent',
                    backgroundColor: selectedStationUuid === station.uuid ? 'rgba(0, 212, 255, 0.05)' : 'background.paper',
                    '&:hover': { 
                      transform: 'translateY(-2px)', 
                      elevation: 4,
                      borderColor: selectedStationUuid === station.uuid ? '#00d4ff' : 'rgba(0, 212, 255, 0.3)'
                    }
                  }}
                >
                  {/* Station Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0, mr: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        {station.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {station.description}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'nowrap', flexShrink: 0 }}>
                      {station.debug && (
                        <Chip
                          icon={<BugReport />}
                          label="Debug"
                          size="small"
                          color="info"
                        />
                      )}
                      <Chip
                        label={station.status}
                        size="small"
                        color={getStatusColor(station.status) as any}
                      />
                      <Chip
                        label={station.public ? 'Public' : 'Private'}
                        size="small"
                        color={station.public ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    </Box>
                  </Box>

                  {/* Location and Details */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocationOn sx={{ fontSize: 16, mr: 0.5 }} color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {station.location?.latitude?.toFixed(4) || 'N/A'}, {station.location?.longitude?.toFixed(4) || 'N/A'}
                        {station.elevation && ` (${station.elevation}m)`}
                      </Typography>
                    </Box>
                    
                    <Typography variant="caption" color="text.secondary">
                      UUID: {station.uuid}
                    </Typography>
                  </Box>

                  {/* Latest Readings */}
                  {station.latest_readings && Object.keys(station.latest_readings).length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Latest Readings:
                      </Typography>
                      <Grid container spacing={1}>
                        {Object.entries(station.latest_readings).slice(0, 4).map(([sensor, reading]: [string, any]) => (
                          <Grid item xs={6} key={sensor}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              p: 1, 
                              bgcolor: 'background.default', 
                              borderRadius: 1 
                            }}>
                              <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'primary.light' }}>
                                {React.cloneElement(getSensorIcon(sensor), { sx: { fontSize: 14 } })}
                              </Avatar>
                              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {formatReading(sensor, reading)}
                                </Typography>
                                <Typography variant="body2" fontWeight="bold" noWrap>
                                  {reading.value} {reading.unit}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* Tags */}
                  {station.tags && station.tags.length > 0 && (
                    <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {station.tags.map((tag, idx) => (
                        <Chip
                          key={idx}
                          label={tag}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(0, 212, 255, 0.1)',
                            color: '#00d4ff',
                            border: '1px solid rgba(0, 212, 255, 0.3)',
                          }}
                        />
                      ))}
                    </Box>
                  )}

                  {/* Action Buttons */}
                  <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ mr: 1 }}
                      onClick={() => handleStationSelect(station.uuid)}
                    >
                      View Details
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => handleStationSelect(station.uuid, 2)}
                    >
                      Time Series
                    </Button>
                  </Box>
                </Paper>
              </Fade>
            </Grid>
          ))}
        </Grid>

            {stations.length === 0 && !loading && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No stations found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure your first environmental monitoring station
                </Typography>
                <Button 
                  variant="contained" 
                  sx={{ mt: 2 }}
                  onClick={() => setAddStationDialogOpen(true)}
                >
                  Add Station
                </Button>
              </Box>
            )}
          </>
        )}

        {/* Map View */}
        {tabValue === 1 && (
          <Box sx={{ position: 'relative' }}>
            <MapView 
              onStationSelect={handleMapStationSelect}
            />
            
            {/* Station Metrics Display Below Map */}
            {selectedMapStation && (
              <Box sx={{ mt: 4 }}>
                <Card sx={{ 
                  background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.8) 0%, rgba(22, 33, 62, 0.8) 100%)',
                  border: '1px solid rgba(0, 212, 255, 0.2)',
                }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" gutterBottom sx={{ 
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #00d4ff 0%, #ff6b6b 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      color: 'transparent',
                    }}>
                      {selectedMapStation.name} - Metrics Dashboard
                    </Typography>
                    
                    {/* Live Readings */}
                    {selectedMapStation.latest_readings && Object.keys(selectedMapStation.latest_readings).length > 0 && (
                      <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom>
                          Current Readings
                        </Typography>
                        <Grid container spacing={2}>
                          {Object.entries(selectedMapStation.latest_readings).map(([sensor, reading]: [string, any]) => (
                            <Grid item xs={12} sm={6} md={3} key={sensor}>
                              <Paper sx={{ p: 2, textAlign: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                                  {getSensorIcon(sensor)}
                                  <Typography variant="subtitle2" sx={{ ml: 1 }}>
                                    {formatReading(sensor, reading)}
                                  </Typography>
                                </Box>
                                <Typography variant="h4" color="primary">
                                  {reading.value} {reading.unit}
                                </Typography>
                                {reading.timestamp && (
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(reading.timestamp).toLocaleString()}
                                  </Typography>
                                )}
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}

                    {/* Time Series Charts - Dynamic */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                        <Typography variant="h6">
                          {getMapTimeRangeLabel()} Sensor Measurements
                        </Typography>
                        <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                          <InputLabel id="map-time-range-label">Time Range</InputLabel>
                          <Select
                            labelId="map-time-range-label"
                            id="map-time-range-select"
                            value={mapTimeRange}
                            onChange={handleMapTimeRangeChange}
                            label="Time Range"
                          >
                            <MenuItem value={24}>Last 24 Hours</MenuItem>
                            <MenuItem value={48}>Last 48 Hours</MenuItem>
                            <MenuItem value={120}>Last 5 Days</MenuItem>
                            <MenuItem value={720}>Last Month</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                      {mapTimeSeriesData.length > 0 ? (
                        <Box>
                          {(() => {
                            const availableMetrics = Object.keys(mapTimeSeriesData[0]).filter(
                              key => key !== 'timestamp' && key !== 'time' &&
                                     mapTimeSeriesData[0][key] !== null &&
                                     mapTimeSeriesData[0][key] !== undefined
                            );

                            // Default colors for metrics without configuration
                            const defaultColors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#a28bd4', '#58d4ff'];

                            return availableMetrics.map((metric, index) => {
                              // Use predefined config if available, otherwise create a default one
                              const config = metricConfig[metric] || {
                                name: metric.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
                                color: defaultColors[index % defaultColors.length],
                                unit: ''
                              };

                              return (
                                <Box key={metric} sx={{ mb: index < availableMetrics.length - 1 ? 4 : 0 }}>
                                  <Typography variant="h6" gutterBottom>
                                    {config.name}
                                  </Typography>
                                  <Box sx={{ height: 300 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                      <LineChart data={mapTimeSeriesData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="time" />
                                        <YAxis />
                                        <Tooltip />
                                        <Line
                                          type="monotone"
                                          dataKey={metric}
                                          stroke={config.color}
                                          strokeWidth={2}
                                          name={config.unit ? `${config.name} (${config.unit})` : config.name}
                                        />
                                      </LineChart>
                                    </ResponsiveContainer>
                                  </Box>
                                </Box>
                              );
                            });
                          })()}
                        </Box>
                      ) : (
                        <Typography color="text.secondary">
                          No time series data available for this station.
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            )}
          </Box>
        )}

        {/* Table View Content */}
        {tabValue === 2 && (
          <Box>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                {/* Filters and Search */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search station or metric..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="filter-station-label">Station</InputLabel>
                      <Select
                        labelId="filter-station-label"
                        id="filter-station-select"
                        value={filterStation}
                        onChange={handleFilterStationChange}
                        label="Station"
                      >
                        <MenuItem value="all">All Stations</MenuItem>
                        {stations.map(station => (
                          <MenuItem key={station.uuid} value={station.uuid}>
                            {station.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="filter-metric-label">Metric</InputLabel>
                      <Select
                        labelId="filter-metric-label"
                        id="filter-metric-select"
                        value={filterMetric}
                        onChange={handleFilterMetricChange}
                        label="Metric"
                      >
                        <MenuItem value="all">All Metrics</MenuItem>
                        {getUniqueMetrics().map(metric => {
                          const config = metricConfig[metric];
                          return config ? (
                            <MenuItem key={metric} value={metric}>
                              {config.name}
                            </MenuItem>
                          ) : null;
                        })}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="table-time-range-label">Time Range</InputLabel>
                      <Select
                        labelId="table-time-range-label"
                        id="table-time-range-select"
                        value={tableTimeRange}
                        onChange={handleTableTimeRangeChange}
                        label="Time Range"
                      >
                        <MenuItem value={1}>Last Hour</MenuItem>
                        <MenuItem value={6}>Last 6 Hours</MenuItem>
                        <MenuItem value={24}>Last 24 Hours</MenuItem>
                        <MenuItem value={48}>Last 48 Hours</MenuItem>
                        <MenuItem value={120}>Last 5 Days</MenuItem>
                        <MenuItem value={720}>Last Month</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {/* Table */}
                {tableLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                    <CircularProgress />
                    <Typography variant="body1" sx={{ ml: 2 }}>
                      Loading measurements...
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Station</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Metric</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {getFilteredTableData()
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row, index) => (
                              <TableRow key={`${row.stationUuid}-${row.timestamp}-${row.metric}-${index}`}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <LocationOn sx={{ mr: 1, fontSize: 18, color: '#00d4ff' }} />
                                    {row.stationName}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={row.metricName}
                                    size="small"
                                    sx={{
                                      backgroundColor: `${metricConfig[row.metric]?.color}20`,
                                      color: metricConfig[row.metric]?.color,
                                      fontWeight: 500,
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {typeof row.value === 'number' ? row.value.toFixed(2) : row.value}
                                  </Typography>
                                </TableCell>
                                <TableCell>{row.unit}</TableCell>
                                <TableCell>
                                  {new Date(row.timestamp).toLocaleString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit',
                                  })}
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {getFilteredTableData().length === 0 && (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                          No measurements found matching the current filters.
                        </Typography>
                      </Box>
                    )}

                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50, 100]}
                      component="div"
                      count={getFilteredTableData().length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Station Details Dialog */}
        <StationDetails
          open={detailsDialogOpen}
          onClose={() => setDetailsDialogOpen(false)}
          stationUuid={selectedStationUuid}
          initialTab={initialDialogTab}
        />

        {/* Add Station Dialog */}
        <AddStationDialog
          open={addStationDialogOpen}
          onClose={() => {
            setAddStationDialogOpen(false);
            setAddStationLocation(undefined);
          }}
          onSubmit={handleCreateStation}
          initialLocation={addStationLocation}
        />

        <StationConfigDialog
          open={configDialogOpen}
          onClose={() => setConfigDialogOpen(false)}
          station={selectedConfigStation}
          onEdit={handleEditStation}
          onDelete={handleDeleteStation}
          onExport={handleExportStation}
          currentUserEmail={userEmail}
        />

        {/* Warning Snackbar for Configure Button */}
        <Snackbar
          open={warningSnackbarOpen}
          autoHideDuration={4000}
          onClose={() => setWarningSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{
            top: '80px !important',
            zIndex: 10002
          }}
        >
          <Alert
            onClose={() => setWarningSnackbarOpen(false)}
            severity="warning"
            variant="filled"
            sx={{ width: '100%' }}
          >
            Please select a station first before configuring
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Dashboard;